import React from 'react';
import { getBrandConfig } from '../utils/brandUtils';

export const TermsOfService: React.FC = () => {
  const brandConfig = getBrandConfig();
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-base-100">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary mb-4">Terms of Service (TOS)</h1>
          <p className="text-lg text-base-content/70">
            {brandConfig.name} - Effective Date: {currentDate}
          </p>
        </div>

        {/* Content */}
        <div className="max-w-none">
          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Overview</h2>
            <p className="mb-4">
              {brandConfig.name} ("Service") is provided by {brandConfig.legalEntity} ("Company," "we," "our," or "us"). The Service provides healthcare professionals with tools for generating SOAP notes using AWS HealthScribe and related features such as PDF population and document management.
            </p>
            <p className="mb-4">
              By creating an account, accessing, or using the Service, you agree to these Terms of Service ("Terms"). If you do not agree, you must not use the Service.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Eligibility</h2>
            <p className="mb-4">
              The Service is intended for healthcare professionals and related organizations.
            </p>
            <p className="mb-4">
              You must be at least 18 years old to create an account.
            </p>
            <p className="mb-4">
              You are responsible for maintaining the confidentiality of your login credentials.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. Accounts</h2>
            <p className="mb-4">
              You must register with a valid email address.
            </p>
            <p className="mb-4">
              Accounts may be terminated at any time upon request by emailing <a href={`mailto:${brandConfig.privacyEmail}`} className="text-primary hover:text-primary-focus">{brandConfig.privacyEmail}</a>.
            </p>
            <p className="mb-4">
              We may suspend or terminate your account for violations of these Terms.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Payments</h2>
            <p className="mb-4">
              Subscription fees are processed through Stripe, our third-party payment processor.
            </p>
            <p className="mb-4">
              We do not store credit card or billing information.
            </p>
            <p className="mb-4">
              All payments are non-refundable unless required by law.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. User Responsibilities</h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Misuse the Service or attempt to access data outside your account scope.</li>
              <li>Upload unlawful, infringing, or harmful content.</li>
              <li>Use the Service for non-healthcare or unauthorized purposes.</li>
            </ul>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Data Retention & Deletion</h2>
            <p className="mb-4">
              SOAP notes are retained for up to 30 days by default.
            </p>
            <p className="mb-4">
              You may delete your data at any time.
            </p>
            <p className="mb-4">
              After deletion, data may remain in backups for a limited period before permanent removal.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Intellectual Property</h2>
            <p className="mb-4">
              We retain all rights, title, and interest in the Service, including software, designs, and trademarks.
            </p>
            <p className="mb-4">
              You retain ownership of the data and documents you upload.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. HIPAA & Compliance</h2>
            <p className="mb-4">
              We comply with HIPAA and have a Business Associate Agreement (BAA) with AWS.
            </p>
            <p className="mb-4">
              You are responsible for obtaining patient consent where required before uploading PHI.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Security</h2>
            <p className="mb-4">
              All data is encrypted at rest and in transit.
            </p>
            <p className="mb-4">
              Access to PHI is restricted to authorized roles only.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Disclaimers</h2>
            <p className="mb-4">
              The Service is provided "as is" without warranties of any kind. We do not guarantee error-free or uninterrupted service.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Limitation of Liability</h2>
            <p className="mb-4">
              To the fullest extent permitted by law, {brandConfig.legalEntity} shall not be liable for indirect, incidental, or consequential damages arising from use of the Service.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Governing Law & Dispute Resolution</h2>
            <p className="mb-4">
              These Terms are governed by the laws of the United States.
            </p>
            <p className="mb-4">
              Any disputes will be resolved through binding arbitration.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Changes</h2>
            <p className="mb-4">
              We may update these Terms at any time. Changes will be posted with a new "Effective Date." Users will be notified by email or in-app notice.
            </p>
          </div>

          <div className="bg-base-200 rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-4">14. Contact</h2>
            <p className="mb-4">Questions? Contact us at:</p>
            <div className="bg-primary/10 rounded-lg p-4 text-center">
              <a 
                href={`mailto:${brandConfig.privacyEmail}`}
                className="text-primary hover:text-primary-focus font-semibold text-lg"
              >
                {brandConfig.privacyEmail}
              </a>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <button 
            onClick={() => window.history.back()} 
            className="btn btn-primary"
          >
            ← Go Back
          </button>
        </div>
      </div>
    </div>
  );
};
